import type { SVGProps } from 'react';

/**
 * Ikony jako wektory w kodzie, nie emoji.
 *
 * Prototyp używał znaków (◱ ☰ ▣ 👤), a te renderują się inaczej na Windowsie,
 * inaczej na Androidzie i inaczej w druku — czyli dokładnie tam, gdzie ta aplikacja
 * ma działać. Kilka ścieżek SVG kosztuje mniej niż jedna niespodzianka na telefonie
 * serwisanta.
 *
 * Wszystkie rysowane obrysem w `currentColor`, więc kolor bierze się z tekstu obok
 * i nigdy nie wprowadza własnego (CLAUDE.md §5).
 */

type Props = Omit<SVGProps<SVGSVGElement>, 'children'> & { rozmiar?: number };

function Ikona({ rozmiar = 18, children, ...reszta }: Props & { children: React.ReactNode }) {
  return (
    <svg
      width={rozmiar}
      height={rozmiar}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...reszta}
    >
      {children}
    </svg>
  );
}

/** Urządzenia — maszyna widziana jako korpus z podstawą. */
export function IkonaUrzadzenia(props: Props) {
  return (
    <Ikona {...props}>
      <rect x="3" y="4" width="18" height="11" rx="0" />
      <path d="M7 15v5M17 15v5M4 20h16M8 8h8" />
    </Ikona>
  );
}

/** Kontrahenci — zakład, nie osoba: klientem jest firma. */
export function IkonaKontrahenci(props: Props) {
  return (
    <Ikona {...props}>
      <path d="M3 21h18M5 21V7l7-4 7 4v14" />
      <path d="M9 21v-5h6v5M9 10h.01M15 10h.01M9 13h.01M15 13h.01" />
    </Ikona>
  );
}

/** Zlecenia serwisowe — kartka ze zgłoszeniem, bo to ona zastępuje papier. */
export function IkonaZlecenia(props: Props) {
  return (
    <Ikona {...props}>
      <path d="M8 3h8a1 1 0 0 1 1 1v1h2a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h2V4a1 1 0 0 1 1-1z" />
      <path d="M8 11h8M8 15h5" />
    </Ikona>
  );
}

/** Montaże — narzędzie, bo montaż dzieje się w warsztacie, nie przy biurku. */
export function IkonaMontaze(props: Props) {
  return (
    <Ikona {...props}>
      <path d="M14.7 6.3a4 4 0 0 1 5 5L21 12l-9 9-3-3 9-9z" />
      <path d="M6.5 3.5 3 7l3 3 3.5-3.5z" />
      <path d="m6 10 5 5" />
    </Ikona>
  );
}

/** Podpis — odręczna kreska nad linią. */
export function IkonaPodpis(props: Props) {
  return (
    <Ikona {...props}>
      <path d="M3 19h18" />
      <path d="M4 15c3-1 4-9 6-9s1 8 3 8 2-4 4-4 2 3 3 3" />
    </Ikona>
  );
}

export function IkonaKonto(props: Props) {
  return (
    <Ikona {...props}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" />
    </Ikona>
  );
}

export function IkonaUstawienia(props: Props) {
  return (
    <Ikona {...props}>
      {/* Suwak, nie zębatka: ustawienia w tej aplikacji to przełączniki, a nie
          panel sterowania silnikiem. Ikona ma mówić, co się za nią kryje. */}
      <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="10" cy="17" r="2" />
    </Ikona>
  );
}

export function IkonaZdjecie(props: Props) {
  return (
    <Ikona {...props}>
      <rect x="3" y="6" width="18" height="14" />
      <circle cx="12" cy="13" r="3.5" />
      <path d="M8 6l1.5-2h5L16 6" />
    </Ikona>
  );
}

export function IkonaSzukaj(props: Props) {
  return (
    <Ikona {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </Ikona>
  );
}

/** Skan — ramka celownika, ta sama metafora co na ekranie aparatu. */
export function IkonaSkan(props: Props) {
  return (
    <Ikona {...props}>
      <path d="M3 8V5a2 2 0 0 1 2-2h3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M8 21H5a2 2 0 0 1-2-2v-3" />
      <path d="M7 12h10" />
    </Ikona>
  );
}

export function IkonaDrukuj(props: Props) {
  return (
    <Ikona {...props}>
      <path d="M6 9V3h12v6M6 18H4v-6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v6h-2" />
      <rect x="6" y="14" width="12" height="7" />
    </Ikona>
  );
}

export function IkonaWstecz(props: Props) {
  return (
    <Ikona {...props}>
      <path d="M19 12H5M11 18l-6-6 6-6" />
    </Ikona>
  );
}

export function IkonaPlus(props: Props) {
  return (
    <Ikona {...props}>
      <path d="M12 5v14M5 12h14" />
    </Ikona>
  );
}

export function IkonaWyloguj(props: Props) {
  return (
    <Ikona {...props}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </Ikona>
  );
}
